import { useEffect, useMemo, useState } from "react";
import { EngagementService } from "@services/engagements/engagementService";
import { KeyDateService } from "@services/engagements/keyDateService";
import type { KeyDate, KeyDateType } from "@models/keyDate.model";
import type { KeyDateRef } from "./emailSmsTypes";

/*
    This campaign's real key dates, in the shape the sequence screens match on (MPN-2204).

    Every send date, every "outside campaign dates" warning and the whole calendar are
    computed from these. They used to come from three fabricated dates in the demo module,
    which meant the same three dates on every campaign in the platform.

    Two endpoints rather than one because the campaign endpoint returns `date_type` as the
    type's *label*, and the screens match a touch's anchor against a type *code*. The type
    list is what supplies the code, so the join happens here rather than in six components.
*/
export function useCampaignKeyDates(launchId: number | null) {
  const [keyDates, setKeyDates] = useState<KeyDate[]>([]);
  const [types, setTypes] = useState<KeyDateType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!launchId) {
      setKeyDates([]);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    void Promise.allSettled([
      new KeyDateService().listForCampaign(launchId),
      new EngagementService().getKeyDateTypes(),
    ]).then(([dates, loadedTypes]) => {
      if (cancelled) return;
      if (dates.status === "fulfilled") setKeyDates(dates.value ?? []);
      if (loadedTypes.status === "fulfilled") setTypes(loadedTypes.value ?? []);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [launchId]);

  const refs = useMemo<KeyDateRef[]>(() => {
    const byId = new Map(types.map((type) => [type.id, type]));
    return keyDates
      .filter((keyDate) => keyDate.date && keyDate.key_date_type_id != null)
      .map((keyDate) => {
        const type = byId.get(keyDate.key_date_type_id as number);
        return {
          // The TYPE's id, not the key date's: a touch anchors to a kind of date, and the
          // row holding it is this campaign's answer for that kind.
          id: keyDate.key_date_type_id as number,
          type: type?.code ?? String(keyDate.key_date_type_id),
          label: keyDate.label?.trim() || type?.label || keyDate.date_type || "",
          date: (keyDate.date as string).slice(0, 10),
        };
      });
  }, [keyDates, types]);

  /*
      Every active type's display name, not just the ones dated on this campaign.

      The missing-key-date warning names a type the campaign has NOT set, so it cannot read
      the name off a key date. Real codes are `term_start` and `registration_end`; without
      this the warning reads "term_start isn't on this campaign."
  */
  const typeLabels = useMemo<Record<string, string>>(
    () => Object.fromEntries(types.map((type) => [type.code, type.label])),
    [types],
  );

  /*
      Type id -> code, over every type rather than the campaign's dated ones.

      A touch anchors to a KIND of date, and it keeps that anchor whether or not this
      campaign has set one. Resolving it against the campaign's key dates instead lost the
      anchor entirely on a campaign with none — which is the case the missing-key-date
      warning exists for, so the warning rendered with a blank name and the trigger's anchor
      dropdown came up empty.
  */
  const codeById = useMemo<Record<number, string>>(
    () => Object.fromEntries(types.map((type) => [type.id, type.code])),
    [types],
  );

  return { keyDates: refs, typeLabels, codeById, isLoading };
}
